import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import QuizForm from '../components/QuizForm';
import BlueprintIcon from '../components/BlueprintIcon';
import { GYM_CONFIG, MEMBERSHIP_TIERS } from '../config';
import membershipSales from '../config/membershipSales.json';
import { SCS_MEDIA, photo } from '../config/media';
import MapEmbed from '../components/MapEmbed';
import { getSiteContent, getBlogPosts, getTeamMembers } from '../lib/api';
import { withoutConsolidated } from '../seo/consolidatedSlugs';
import { MEMBER_STORIES } from '../config/testimonials';
import { trackBookTourClick, trackPhoneClick, trackDirectionsClick } from '../utils/analytics';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { ArrowRight, MapPin, Phone, Star } from 'lucide-react';
import { preloaded } from '../lib/preload';

const LOGO_URL = SCS_MEDIA.logo;

const FAQ_ITEMS = [
  { q: 'I have never lifted before. Can I start here?', a: 'Yes. You can tour the gym first, learn how the space works, and decide whether independent training or coaching is the better starting point for you.' },
  { q: 'Do I have to be social to feel welcome?', a: 'No. You can ask for help when you need it and still keep your session private and focused.' },
  { q: 'Do I need to choose one training discipline?', a: 'No. Powerlifting, Olympic weightlifting, strongman, and general strength work are ways members use the same facility.' },
  { q: 'Is personal training required?', a: 'No. Personal training is available if you want individual coaching. Members can also follow their own programs.' },
  { q: 'Can I see the gym before I join?', a: 'Yes. Book a free facility tour to walk the floor, see the equipment, and ask questions.' },
];

/* Facts the entity paragraph asserts, restated as scannable proof.
   Each one is already true somewhere else on this page or in the
   business configuration, which is the condition for repeating it in
   a compressed form. Nothing here is a number nobody has counted, and
   the two claims the owner has not confirmed, 24/7 member access and
   the day pass window, are absent on purpose. */
const ENTITY_FACTS = [
  { label: 'Where', value: 'Harvey West, Santa Cruz CA' },
  { label: 'Floor', value: 'Racks, bars, platforms, open gym' },
  { label: 'Disciplines', value: 'Powerlifting, weightlifting, strongman, general strength' },
  { label: 'Access', value: 'Day passes through annual memberships' },
];

/* What a tour walks past, indexed beside the photograph.
 *
 * The brief asks for interactive hotspots pinned to points on this
 * frame. They were built and then removed, because a marker is a
 * spatial claim: putting one at 62% across and 13% down and labelling
 * it "the wall seal" asserts that the seal is at that point in that
 * crop, and at desktop it landed on bare wall. Nobody has measured
 * these positions against the photograph, and a confident pin on the
 * wrong object is worse than no pin.
 *
 * So the facts stay, indexed and readable, and the pins wait for a
 * wide facility frame with positions checked against it. Recorded in
 * the evidence gap log rather than shipped on a guess. */
const WALKTHROUGH_POINTS = [
  { label: 'The wall seal', fact: 'The painted SCS seal above the green stripe. It is the first thing you see from the door.' },
  { label: 'Plate storage', fact: 'Bumper and iron plates stored at floor level, next to the equipment that uses them.' },
  { label: 'Racks', fact: 'Power racks along the wall, set up for squat, bench and pressing work.' },
  { label: 'Open floor', fact: 'Platform and open floor space, which is where deadlifts and Olympic work happen.' },
];

const DISCIPLINES = [
  { icon: 'rack', title: 'Powerlifting', desc: 'Practice the squat, bench press, and deadlift with racks, benches, barbells, and plates.' },
  { icon: 'platform', title: 'Olympic weightlifting', desc: 'Train the snatch and clean and jerk on a floor built for barbell work.' },
  { icon: 'open-floor', title: 'Strongman', desc: 'Build usable strength through varied implements and event-focused training.' },
  { icon: 'general-strength', title: 'General strength', desc: 'Build strength for whatever life asks of you.' },
];

const STARTING_POINTS = [
  {
    icon: 'first-timer',
    number: '01',
    title: 'Starting your first program',
    desc: 'You do not need to arrive fit or experienced. Learn the room at your pace and ask questions when you need to.',
    tone: 'white',
  },
  {
    icon: 'own-program',
    number: '02',
    title: 'Returning to training',
    desc: 'Come back without having to prove where you used to be. Build from the capacity you have today.',
    tone: 'mint',
  },
  {
    icon: 'general-strength',
    number: '03',
    title: 'Following your own plan',
    desc: 'Use the equipment, manage your session, and train without a class schedule or a social expectation.',
    tone: 'deep',
  },
  {
    icon: 'competitor',
    number: '04',
    title: 'Working toward a strength goal',
    desc: "Train the details that matter to you with room for focused practice and people who understand what you're working toward.",
    tone: 'white',
  },
];

const PREVIEW_PLAN_IDS = ['daypass', 'huscler-12', 'annual'];

// New membership purchases are paused. The plan preview below is retained,
// unrendered, so resuming is one boolean in src/config/membershipSales.json.
const SALES_PAUSED = membershipSales.newMembershipSalesPaused;
const WAITLIST = membershipSales.waitlist;

export default function Home() {
  const [c, setC] = useState(() => preloaded('content', {}));
  const copy = (key, approved) => c[key] || approved;
  // Filtered and sliced here exactly as the effect below does it, from a
  // payload captured with the same limit the effect asks for. Anything else and
  // the server would render a different three posts than the browser, which is
  // a hydration mismatch dressed up as a content bug.
  const [blogPosts, setBlogPosts] = useState(() => withoutConsolidated(preloaded('postsHome', [])).slice(0, 3));
  const [teamMembers, setTeamMembers] = useState(() => preloaded('team', []).filter(m => m.visible !== false));

  useEffect(() => {
    getSiteContent().then(({ data }) => setC(data)).catch(() => {});
    getBlogPosts({ limit: 8 }).then(r => setBlogPosts(withoutConsolidated(r.data.posts || []).slice(0, 3))).catch(() => {});
    getTeamMembers().then(({ data }) => setTeamMembers(data.filter(m => m.visible !== false))).catch(() => {});
  }, []);

  const previewPlans = PREVIEW_PLAN_IDS
    .map((id) => MEMBERSHIP_TIERS.find((tier) => tier.id === id))
    .filter(Boolean);

  const [featuredPost, ...otherPosts] = blogPosts;

  return (
    <div className="min-h-screen" style={{ background: 'var(--scs-bg)' }}>
      <Navbar />

      <main id="main">

        {/* ------------------------------------------------------------------
            1. HERO

            The room, at the size the room deserves, with the headline
            sitting in the dark half of the frame rather than over the
            busy half. The photograph is an img rather than a CSS
            background so it can carry width, height, a source set and
            a high fetch priority: it is the Largest Contentful Paint
            on this page and a background image is invisible to the
            preload scanner.
            ------------------------------------------------------------------ */}
        <section data-testid="home-hero" className="relative pt-16 on-dark on-photo" style={{ backgroundColor: 'var(--scs-forest-deep)' }}>
          <div className="relative min-h-[380px] sm:min-h-[64vh] lg:min-h-[72vh] max-h-[760px] flex items-end overflow-hidden">
            {/* Image layer: zoomed on mobile to crop ceiling, unscaled on desktop */}
            <div className="absolute inset-0 overflow-hidden">
              <img
                {...photo('facility', { sizes: '100vw', eager: true })}
                alt="The Santa Cruz Strength training floor, with racks, plate storage and the painted wall seal"
                className="w-full h-full object-cover scs-shift scs-hero-crop scs-hero-zoom"
                style={{ filter: 'saturate(0.62) contrast(1.06) brightness(0.66)' }}
              />
            </div>
            {/* Two overlays rather than one flat scrim: a vertical fade that
                anchors the type, and a forest wash that ties the photograph
                to the palette instead of leaving it a grey rectangle. */}
            <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(6,48,31,0.94) 0%, rgba(6,48,31,0.70) 42%, rgba(6,48,31,0.28) 100%)' }} />
            <div className="absolute inset-0" style={{ backgroundColor: 'rgba(14,93,62,0.20)', mixBlendMode: 'multiply' }} />

            <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 w-full pb-12 sm:pb-16 pt-24">
              <div className="max-w-2xl">
                <h1
                  className="font-display mb-5"
                  style={{ color: 'var(--scs-white)', fontSize: 'clamp(2.25rem, 6.2vw, 4.25rem)', lineHeight: 0.94, letterSpacing: '-0.005em' }}
                >
                  {copy('home_hero_headline_v2', 'Strength for every starting point.')}
                </h1>
                <p className="text-base sm:text-lg mb-8 leading-relaxed max-w-xl" style={{ color: 'var(--scs-text-on-dark)' }}>
                  {copy('home_hero_subtitle_v2', 'Beginners, returning lifters, independent members, and experienced strength athletes share the floor at Santa Cruz Strength. Train toward your own goals in a gym where people respect the work and one another.')}
                </p>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                  <Link to="/contact#tour-request" data-testid="home-hero-book-visit-button"
                    className="btn-clay px-7 text-sm uppercase tracking-wider font-semibold w-full sm:w-auto"
                    onClick={() => trackBookTourClick('hero')}>
                    Book a Free Facility Tour <ArrowRight size={15} />
                  </Link>
                  <Link to="/join" className="btn-outline btn-outline-on-dark px-7 text-sm w-full sm:w-auto">
                    {SALES_PAUSED ? copy('membership_waitlist_cta', WAITLIST.cta) : 'View Memberships'}
                  </Link>
                </div>
                <div className="flex flex-wrap gap-3 mt-6" data-testid="home-identity-labels">
                  {['Independent', 'Strength-Focused', 'Community-Minded', 'Santa Cruz'].map((label) => (
                    <span key={label} className="text-xs font-semibold uppercase tracking-[0.12em] px-3 py-1.5" style={{ color: 'var(--scs-mint)', border: '1px solid rgba(163,211,181,0.40)', borderRadius: '2px' }}>{label}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ------------------------------------------------------------------
            2. DECISION STRIP
            Address and phone, immediately under the fold line, because
            the first question a local visitor has is where this is.
            ------------------------------------------------------------------ */}
        <section className="py-4 on-dark" style={{ background: 'var(--scs-forest)' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <p className="flex items-center gap-2 m-0">
              <MapPin size={15} style={{ color: 'var(--scs-mint)' }} aria-hidden="true" />
              <span className="text-sm" style={{ color: 'var(--scs-text-on-dark)' }}>151 Harvey West Blvd Ste D, Santa Cruz, CA 95060</span>
            </p>
            <div className="flex items-center gap-5">
              <Link to="/contact#tour-request" className="text-sm font-semibold underline underline-offset-4 decoration-1 py-1.5 inline-block" style={{ color: 'var(--scs-mint)' }}>
                Book a Free Facility Tour
              </Link>
              <a href={GYM_CONFIG.phoneHref} onClick={() => trackPhoneClick()} className="flex items-center gap-1.5 text-sm py-1.5" style={{ color: 'var(--scs-text-on-dark)' }}>
                <Phone size={14} aria-hidden="true" />{GYM_CONFIG.phone}
              </a>
            </div>
          </div>
        </section>

        {/* ------------------------------------------------------------------
            2. WHO WE ARE
            ------------------------------------------------------------------ */}
        <section className="py-16 sm:py-24" style={{ background: 'var(--scs-cream)' }} data-testid="home-entity-definition">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="max-w-[68ch]">
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mb-6" style={{ color: 'var(--scs-forest)', lineHeight: 0.98 }}>
                A local strength gym built around the people who train here.
              </h2>
              <p className="text-base sm:text-lg leading-relaxed mb-4" style={{ color: 'var(--scs-text)' }}>
                Santa Cruz Strength has been part of the local strength community for more than 13 years. We are an independent gym with the equipment and space to train strength well. Members use the floor for powerlifting, Olympic weightlifting, strongman, general strength work, and the programs they bring with them.
              </p>
              <p className="text-base leading-relaxed" style={{ color: 'var(--scs-text-muted)' }}>
                Different goals, schedules, and experience levels share one room. Members give each other space to focus, offer a spot when asked, and help people find their footing without turning training into a performance.
              </p>
            </div>
          </div>
        </section>

        {/* ------------------------------------------------------------------
            3. WHY WE ARE DIFFERENT
            ------------------------------------------------------------------ */}
        <section className="py-16 sm:py-24" style={{ background: 'var(--scs-sand)' }} data-testid="home-community-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mb-10" style={{ color: 'var(--scs-forest)', lineHeight: 0.96 }}>
              A gym where you can train your way.
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-12">
              <article className="p-7" style={{ background: 'var(--scs-white)', border: '1px solid var(--scs-border)', borderRadius: 'var(--scs-radius-card)' }} data-testid="home-card-ask-for-help">
                <h3 className="font-display-medium text-lg mb-2" style={{ color: 'var(--scs-forest)' }}>Ask for help</h3>
                <p className="text-sm leading-relaxed m-0" style={{ color: 'var(--scs-text-muted)' }}>Need a spot, a second set of eyes, or help finding something? Ask. We look out for one another here.</p>
              </article>
              <article className="p-7" style={{ background: 'var(--scs-white)', border: '1px solid var(--scs-border)', borderRadius: 'var(--scs-radius-card)' }} data-testid="home-card-train-your-way">
                <h3 className="font-display-medium text-lg mb-2" style={{ color: 'var(--scs-forest)' }}>Train Your Way</h3>
                <p className="text-sm leading-relaxed m-0" style={{ color: 'var(--scs-text-muted)' }}>Bring your program, put your headphones on, and focus. You can be part of the gym without being pushed to socialize. Your training is your own, and we are here to support it.</p>
              </article>
              <article className="p-7" style={{ background: 'var(--scs-white)', border: '1px solid var(--scs-border)', borderRadius: 'var(--scs-radius-card)' }} data-testid="home-card-share-the-floor">
                <h3 className="font-display-medium text-lg mb-2" style={{ color: 'var(--scs-forest)' }}>Share the floor</h3>
                <p className="text-sm leading-relaxed m-0" style={{ color: 'var(--scs-text-muted)' }}>Beginners and experienced lifters use the same space. Different goals can coexist when people communicate and respect the room.</p>
              </article>
            </div>

            <p className="text-base leading-relaxed max-w-[68ch]" style={{ color: 'var(--scs-text)' }} data-testid="home-belonging-statement">
              There is no single type of person who joins Santa Cruz Strength. We are a queer-friendly space, and we are committed to creating an environment where all identities are respected.
            </p>
          </div>
        </section>

        {/* ------------------------------------------------------------------
            4. WHO BELONGS HERE
            ------------------------------------------------------------------ */}
        <section className="py-16 sm:py-24" style={{ background: 'var(--scs-cream)' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mb-4 max-w-2xl" style={{ color: 'var(--scs-forest)', lineHeight: 0.98 }}>
              If you have a body, you belong here.
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-10">
              {STARTING_POINTS.map((item, index) => {
                const dark = item.tone === 'deep';
                const background = item.tone === 'white'
                  ? 'var(--scs-white)'
                  : item.tone === 'mint' ? 'var(--scs-mint)' : 'var(--scs-forest-deep)';
                return (
                  <article
                    key={item.title}
                    data-reveal
                    className={`p-7 flex flex-col ${dark ? 'on-dark' : ''}`}
                    style={{
                      background,
                      border: dark ? 'none' : '1px solid var(--scs-border)',
                      borderRadius: 'var(--scs-radius-card)',
                    }}
                  >
                    <div className="flex items-start justify-between mb-6">
                      <BlueprintIcon name={item.icon} size={64} draw className={dark ? 'scs-blueprint-light' : ''} />
                      <span
                        className="font-display text-3xl leading-none"
                        style={{ color: dark ? 'var(--scs-coral-bright)' : 'rgba(14,93,62,0.30)' }}
                        aria-hidden="true"
                      >
                        {item.number}
                      </span>
                    </div>
                    <h3 className="font-display-medium text-xl mb-2" style={{ color: dark ? 'var(--scs-white)' : 'var(--scs-forest)' }}>
                      {item.title}
                    </h3>
                    <p className="text-sm leading-relaxed m-0" style={{ color: dark ? 'var(--scs-text-on-dark-muted)' : 'var(--scs-text-muted)' }}>
                      {item.desc}
                    </p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* ------------------------------------------------------------------
            5. WHAT YOU CAN TRAIN
            ------------------------------------------------------------------ */}
        <section className="py-16 sm:py-24 on-dark" style={{ background: 'var(--scs-forest-deep)' }} data-testid="home-disciplines">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-12">
              <h2 className="lg:col-span-5 font-display text-3xl sm:text-4xl lg:text-5xl m-0" style={{ color: 'var(--scs-white)', lineHeight: 0.96 }}>
                Whatever strength means to you, you can train it here.
              </h2>
              <p className="lg:col-span-6 lg:col-start-7 text-base leading-relaxed m-0 self-end" style={{ color: 'var(--scs-text-on-dark-muted)' }}>
                These are different ways members use the same strength-focused facility. The floor has room for all of it.
              </p>
            </div>

            <ol className="scs-rail scs-rail-h relative grid grid-cols-1 lg:grid-cols-4 gap-10 lg:gap-8 pl-10 lg:pl-0" style={{ color: 'var(--scs-mint)' }}>
              {DISCIPLINES.map((item, index) => (
                <li key={item.title} data-reveal>
                  <span className="scs-rail-node absolute lg:relative" style={{ left: 0, marginLeft: '-1px' }} aria-hidden="true" />
                  <div className="lg:mt-7">
                    <BlueprintIcon name={item.icon} size={72} draw className="scs-blueprint-light mb-5" />
                    <h3 className="font-display-medium text-lg mb-2" style={{ color: 'var(--scs-white)' }}>{item.title}</h3>
                    <p className="text-sm leading-relaxed m-0" style={{ color: 'var(--scs-text-on-dark-muted)' }}>{item.desc}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* ------------------------------------------------------------------
            6. HOW MEMBERSHIP WORKS
            ------------------------------------------------------------------ */}
        <section className="py-16 sm:py-24" style={{ background: 'var(--scs-mint)' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-10">
              <h2 className="lg:col-span-6 font-display text-3xl sm:text-4xl lg:text-5xl m-0" style={{ color: 'var(--scs-forest)', lineHeight: 0.96 }}>
                {SALES_PAUSED
                  ? copy('membership_waitlist_heading', WAITLIST.heading)
                  : 'Membership that fits how you train.'}
              </h2>
            </div>

            {SALES_PAUSED ? (
              <div className="p-7 mb-10" data-testid="home-membership-pause-notice"
                style={{ background: 'var(--scs-white)', border: '1px solid var(--scs-forest)', borderRadius: 'var(--scs-radius-card)' }}>
                <p className="text-base leading-relaxed max-w-[68ch] m-0" style={{ color: 'var(--scs-text)' }}>
                  {copy('membership_waitlist_body', WAITLIST.body)}
                </p>
              </div>
            ) : (
              <>
            <ol className="scs-rail scs-rail-h grid grid-cols-1 sm:grid-cols-3 gap-8 sm:gap-6 mb-12 pl-10 sm:pl-0" style={{ color: 'var(--scs-forest)' }}>
              {[
                { n: '1', h: 'See the gym', b: 'Walk the floor, see the equipment, and ask the questions that matter to you.' },
                { n: '2', h: 'Review your options', b: 'Understand the access, support, prices, fees, and terms before you decide.' },
                { n: '3', h: 'Choose what fits', b: 'Select the membership that matches how you want to train.' },
              ].map(s => (
                <li key={s.n}>
                  <span className="scs-rail-node absolute sm:relative" style={{ left: 0, marginLeft: '-1px' }} aria-hidden="true" />
                  <div className="sm:mt-6">
                    <h3 className="font-display-medium text-lg mb-1" style={{ color: 'var(--scs-forest)' }}>{s.h}</h3>
                    <p className="text-sm leading-relaxed m-0" style={{ color: 'var(--scs-text-muted)' }}>{s.b}</p>
                  </div>
                </li>
              ))}
            </ol>

            <ul className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
              {previewPlans.map((plan, index) => (
                <li
                  key={plan.id}
                  data-reveal
                  className="scs-lock p-7 flex flex-col"
                  data-locked={plan.featured ? 'true' : 'false'}
                  style={{
                    background: 'var(--scs-white)',
                    border: plan.featured ? '1px solid var(--scs-forest)' : '1px solid var(--scs-border)',
                    borderRadius: 'var(--scs-radius-card)',
                  }}
                >
                  {plan.tag && (
                    <span className="text-[11px] font-semibold uppercase tracking-[0.14em] mb-3" style={{ color: plan.featured ? 'var(--scs-coral)' : 'var(--scs-text-muted)' }}>
                      {plan.tag}
                    </span>
                  )}
                  <h3 className="font-display-medium text-xl mb-1" style={{ color: 'var(--scs-forest)' }}>{plan.name}</h3>
                  {plan.subtitle && <p className="text-sm m-0 mb-5" style={{ color: 'var(--scs-text-muted)' }}>{plan.subtitle}</p>}
                  <p className="flex items-baseline gap-2 m-0 mb-1">
                    <span className="font-display text-4xl" style={{ color: 'var(--scs-ink)' }}>{plan.price}</span>
                    <span className="text-sm" style={{ color: 'var(--scs-text-muted)' }}>{plan.cadence}</span>
                  </p>
                  {plan.note && <p className="text-xs m-0 mb-4" style={{ color: 'var(--scs-text-muted)' }}>{plan.note}</p>}
                  <ul className="mt-4 mb-6 flex-1">
                    {plan.terms.map((term) => (
                      <li key={term} className="text-sm py-1.5 pl-4 relative leading-relaxed" style={{ color: 'var(--scs-text)' }}>
                        <span className="absolute left-0 top-3 w-1.5 h-1.5" style={{ background: 'var(--scs-forest)' }} aria-hidden="true" />
                        {term}
                      </li>
                    ))}
                  </ul>
                  <Link to="/join" className={plan.featured ? 'btn-primary text-sm w-full' : 'btn-outline text-sm w-full'}>
                    See {plan.name} terms
                  </Link>
                </li>
              ))}
            </ul>
              </>
            )}

            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/join" className="btn-primary px-7 text-sm" data-testid="home-membership-cta">
                {SALES_PAUSED
                  ? <>{copy('membership_waitlist_cta', WAITLIST.cta)} <ArrowRight size={15} /></>
                  : <>Compare All Memberships <ArrowRight size={15} /></>}
              </Link>
              <Link to="/contact#tour-request" className="btn-outline px-7 text-sm" onClick={() => trackBookTourClick('membership_preview')}>
                Book a Free Facility Tour
              </Link>
            </div>
          </div>
        </section>

        {/* ------------------------------------------------------------------
            7. MEET THE PEOPLE
            ------------------------------------------------------------------ */}
        {teamMembers.length > 0 && (
          <section className="py-16 sm:py-24" style={{ background: 'var(--scs-mint)' }} data-testid="home-people-section">
            <div className="max-w-7xl mx-auto px-4 sm:px-6">
              <h2 className="font-display text-3xl sm:text-4xl mb-10" style={{ color: 'var(--scs-forest)', lineHeight: 0.96 }}>
                Meet the people who keep the gym running.
              </h2>
              <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6 sm:gap-8">
                {teamMembers.map(t => (
                  <li key={t.id} data-testid={`home-person-${t.id}`}>
                    <div className="w-full aspect-square overflow-hidden mb-4 scs-frame">
                      {t.photo_url
                        ? <img src={t.photo_url} alt={`${t.name}, ${t.role}`} className="w-full h-full object-cover object-top scs-photo" loading="lazy" />
                        : <div className="w-full h-full grid place-items-center" style={{ background: 'var(--scs-cream)' }}>
                            <span className="font-display text-2xl" style={{ color: 'var(--scs-forest)' }}>{(t.name || '').split(' ').map(n => n[0]).join('')}</span>
                          </div>}
                    </div>
                    <h3 className="font-display-medium text-base m-0" style={{ color: 'var(--scs-forest)' }}>{t.name}</h3>
                    <p className="text-xs uppercase tracking-[0.10em] mt-1 m-0" style={{ color: 'var(--scs-text-muted)' }}>{t.role}</p>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}

        {/* ------------------------------------------------------------------
            8. MEMBER STORIES
            ------------------------------------------------------------------ */}
        <section className="py-16 sm:py-24" style={{ background: 'var(--scs-cream)' }} data-testid="home-testimonials-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <h2 className="font-display text-3xl sm:text-4xl mb-2" style={{ color: 'var(--scs-forest)' }}>What members say about training here.</h2>
            <p className="text-sm mb-10" style={{ color: 'var(--scs-text-muted)' }}>Published as written. Nothing edited.</p>

            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 list-none p-0 m-0">
              {MEMBER_STORIES.map((story, index) => (
                <li key={story.name} data-reveal>
                  <figure
                    data-testid={`home-testimonial-${story.name.split(' ')[0].toLowerCase()}`}
                    className="m-0 h-full flex flex-col p-6 sm:p-7"
                    style={{
                      background: index === 0 ? 'var(--scs-mint)' : 'var(--scs-white)',
                      border: index === 0 ? '1px solid transparent' : '1px solid var(--scs-border)',
                      borderRadius: 'var(--scs-radius-card)',
                    }}
                  >
                    {/* Five stars, aria-hidden, exactly as the live site
                        publishes them. There is no rating field behind any of
                        these reviews, so the stars are presentation rather
                        than data, and a screen reader announcing a rating the
                        data does not hold would be asserting something untrue.
                        Kept rather than removed because the owner asked for
                        them on the source these were transcribed from. */}
                    <div className="flex gap-1 mb-4" aria-hidden="true">
                      {[0, 1, 2, 3, 4].map(i => (
                        <Star key={i} size={14} strokeWidth={0} style={{ fill: 'var(--scs-coral)' }} />
                      ))}
                    </div>
                    <blockquote className="text-base leading-relaxed m-0 mb-6 flex-1" style={{ color: 'var(--scs-text)' }}>
                      {story.quote}
                    </blockquote>
                    <figcaption
                      className="pt-4"
                      style={{ borderTop: index === 0 ? '1px solid rgba(14,93,62,0.22)' : '1px solid var(--scs-border)' }}
                    >
                      <span className="text-sm font-semibold block" style={{ color: 'var(--scs-forest)' }}>{story.name}</span>
                      <span className="text-xs" style={{ color: 'var(--scs-text-muted)' }}>{story.detail}</span>
                    </figcaption>
                  </figure>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* ------------------------------------------------------------------
            9. BEGINNER RESOURCE
            ------------------------------------------------------------------ */}
        <section className="py-16 sm:py-20" style={{ background: 'var(--scs-cream)' }} data-testid="home-beginner-resource">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-7">
                <BlueprintIcon name="first-timer" size={56} draw className="mb-5" />
                <h2 className="font-display text-2xl sm:text-3xl mb-4" style={{ color: 'var(--scs-forest)', lineHeight: 0.98 }}>New to strength training? Start here.</h2>
                <p className="text-base leading-relaxed max-w-[62ch]" style={{ color: 'var(--scs-text)' }}>
                  You do not have to get fit before you join a gym. The beginner guide explains what your first visit can feel like, what to bring, and how to begin without pretending you already know the room.
                </p>
                <Link to="/blog/beginner-strength-training-santa-cruz" className="scs-advance text-sm font-semibold inline-flex items-center gap-2 mt-4 py-1.5" style={{ color: 'var(--scs-forest)' }}>
                  Read the beginner guide <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* ------------------------------------------------------------------
            10. COMMON QUESTIONS
            ------------------------------------------------------------------ */}
        <section className="py-16 sm:py-20" style={{ background: 'var(--scs-cream)' }}>
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <h2 className="font-display text-2xl sm:text-3xl mb-8" style={{ color: 'var(--scs-forest)' }} data-testid="home-faq-accordion">Common Questions</h2>
            <Accordion type="single" collapsible>
              {FAQ_ITEMS.map(item => (
                <AccordionItem key={item.q} value={item.q} style={{ borderBottom: '1px solid rgba(14,93,62,0.22)' }}>
                  <AccordionTrigger className="text-base font-semibold py-5 hover:no-underline text-left" style={{ color: 'var(--scs-forest)' }}>{item.q}</AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed pb-5 max-w-[68ch]" style={{ color: 'var(--scs-text-muted)' }}>{item.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* ------------------------------------------------------------------
            11. VISIT INVITATION
            ------------------------------------------------------------------ */}
        <section className="py-16 sm:py-20 on-dark" style={{ background: 'var(--scs-forest)' }} data-testid="home-visit-invitation">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="font-display text-2xl sm:text-3xl mb-5" style={{ color: 'var(--scs-white)' }}>Come see where you can train.</h2>
            <p className="text-base sm:text-lg leading-relaxed max-w-2xl mx-auto mb-6" style={{ color: 'var(--scs-text-on-dark)' }}>
              Walk the floor, see the equipment, and ask the questions that matter to you. A tour gives you a direct way to experience the gym and see if we are a good fit.
            </p>
            <Link to="/contact#tour-request" className="btn-clay px-7 text-sm" onClick={() => trackBookTourClick('visit_invitation')}>
              Book a Free Facility Tour <ArrowRight size={15} />
            </Link>
          </div>
        </section>

        {/* ------------------------------------------------------------------
            TOUR REQUEST AND LOCATION
            ------------------------------------------------------------------ */}
        <section className="py-16 sm:py-24 on-dark" style={{ background: 'var(--scs-forest-deep)' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
              <div className="lg:col-span-7 p-6 sm:p-9" style={{ background: 'var(--scs-white)', borderRadius: 'var(--scs-radius-card)', color: 'var(--scs-text)' }}>
                <h2 className="font-display text-2xl sm:text-3xl mb-2" style={{ color: 'var(--scs-forest)' }}>
                  Request Your Free Facility Tour
                </h2>
                <p className="text-sm mb-6" style={{ color: 'var(--scs-text-muted)' }}>
                  Fill out the form and a team member will follow up.
                </p>
                <QuizForm source="book_a_tour" noAutoFocus />
              </div>

              <div className="lg:col-span-5 flex flex-col">
                <div className="flex items-center gap-3 mb-6">
                  <img src={LOGO_URL} alt="" width="40" height="40" className="w-10 h-10 object-contain" style={{ filter: 'brightness(0) invert(1)' }} />
                  <div>
                    <p className="font-display-medium text-base m-0" style={{ color: 'var(--scs-white)' }}>Santa Cruz Strength</p>
                    <p className="text-xs m-0" style={{ color: 'var(--scs-text-on-dark-muted)' }}>Strength Gym</p>
                  </div>
                </div>
                <ul className="mb-6">
                  <li className="flex items-start gap-3 py-3" data-testid="contact-address-block" style={{ borderTop: '1px solid var(--scs-border-dark)' }}>
                    <MapPin size={16} className="mt-0.5 shrink-0" style={{ color: 'var(--scs-mint)' }} aria-hidden="true" />
                    <span className="text-sm" style={{ color: 'var(--scs-text-on-dark)' }}>{GYM_CONFIG.address.full}</span>
                  </li>
                  <li className="py-3" style={{ borderTop: '1px solid var(--scs-border-dark)' }}>
                    <a href={GYM_CONFIG.phoneHref} data-testid="contact-click-to-call-button" className="flex items-center gap-3">
                      <Phone size={16} style={{ color: 'var(--scs-mint)' }} aria-hidden="true" />
                      <span className="text-sm" style={{ color: 'var(--scs-text-on-dark)' }}>{GYM_CONFIG.phone}</span>
                    </a>
                  </li>
                  <li className="flex items-start gap-3 py-3" data-testid="contact-hours-block" style={{ borderTop: '1px solid var(--scs-border-dark)', borderBottom: '1px solid var(--scs-border-dark)' }}>
                    <BlueprintIcon name="building" size={16} className="mt-0.5 shrink-0 scs-blueprint-light" />
                    <span className="text-sm" style={{ color: 'var(--scs-text-on-dark-muted)' }}>Contact for current staffed hours</span>
                  </li>
                </ul>
                <a
                  href="https://maps.google.com/?q=151+Harvey+West+Blvd+Ste+D+Santa+Cruz+CA+95060"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackDirectionsClick('home_location')}
                  className="btn-outline btn-outline-on-dark px-5 text-sm mb-5 w-full sm:w-auto"
                >
                  <MapPin size={14} aria-hidden="true" /> Get Directions
                </a>
                <MapEmbed testId="home-map-embed" className="flex-1 min-h-[280px]" />
              </div>
            </div>
          </div>
        </section>

      </main>

      <Footer />
    </div>
  );
}
